// F1 App-Shell (TES-948): zentrales Shell-Modell.
// Buendelt alle Ableitungen/State, die zuvor inline in AppLayout.tsx lebten
// (Tenant-Logo/Name, GuestMode/TenantContext, Badge-Counts + Pulse, Rollen,
// Produktionsmodus). Verlustfrei extrahiert — gleiche Hooks, gleiche Logik.
// Tenant-/GuestMode-Fetch in useTenantShellData, Produktionsmodus in
// useProductionMode ausgelagert (Komplexitaet/Duplikat-Vermeidung).
import { useAvatarProxyUrl } from "@/hooks/useImageProxyUrl";
import { useAuth } from "@/hooks/useAuth";
import { useOpenInspections, useOpenDefects, useOpenTasks } from "@/hooks/useOpenCounts";
import { useBadgePulse } from "@/hooks/useBadgePulse";
import { useOnlineStatus } from "@/hooks/useOnlineStatus";
import { useTranslation } from "react-i18next";
import { resolvePrimaryRole, resolveDisplayName, resolveInitials, type AppRole } from "./navConfig";
import { useTenantShellData } from "./useTenantShellData";
import { useProductionMode } from "./useProductionMode";

export interface ShellModel {
  t: ReturnType<typeof useTranslation>["t"];
  // Auth / Rollen
  roles: string[];
  isAdmin: boolean;
  isQualitaetsmanager: boolean;
  isSuperAdmin: boolean;
  primaryRole: AppRole;
  displayName: string;
  initials: string;
  signOut: () => void;
  // Tenant
  isGuestMode: boolean;
  isTenantContext: boolean;
  tenantName: string | null;
  displayLogo: string | null | undefined;
  avatarProxyUrl: string | null | undefined;
  profileUpdatedAt: string | undefined;
  // Status / Counts
  isOnline: boolean;
  isInProductionMode: boolean | null;
  openTasks: number;
  openInspections: number;
  openDefects: number;
  overdueTasksCount: number;
  overdueInspectionsCount: number;
  overdueDefectsCount: number;
  isTasksPulsing: boolean;
  isInspectionsPulsing: boolean;
  isDefectsPulsing: boolean;
}

export function useShellModel(): ShellModel {
  const { t } = useTranslation();
  const { profile, roles, signOut, isAdmin, isQualitaetsmanager, isSuperAdmin, user } = useAuth();
  const { isOnline } = useOnlineStatus();
  const { count: openTasks, overdueCount: overdueTasksCount } = useOpenTasks();
  const { openCount: openInspections, overdueCount: overdueInspectionsCount } =
    useOpenInspections();
  const { openCount: openDefects, overdueCount: overdueDefectsCount } = useOpenDefects();

  // Optional-Profilfelder in EINEM Nullish-Schritt aufloesen — die wiederholten `profile?.`-
  // Zugriffe zaehlt fallow je als eigenen Branch (Komplexitaet). `profile ?? {}` destrukturiert
  // verlustfrei (fehlende Felder bleiben undefined, exakt wie bei `profile?.x`).
  const {
    active_tenant_id: activeTenantId,
    avatar_url: avatarUrl,
    updated_at: profileUpdatedAt,
  } = profile ?? {};
  const userId = user?.id;

  const { tenantName, displayLogo, isGuestMode } = useTenantShellData({
    activeTenantId,
    userId,
    isSuperAdmin,
  });
  const avatarProxyUrl = useAvatarProxyUrl(avatarUrl);
  const isInProductionMode = useProductionMode();

  // isTenantContext: Super-Admin has an active tenant set (regardless of membership)
  const isTenantContext = isSuperAdmin && !!activeTenantId;

  // Pulse animations when counts change
  const isTasksPulsing = useBadgePulse(openTasks);
  const isInspectionsPulsing = useBadgePulse(openInspections);
  const isDefectsPulsing = useBadgePulse(openDefects);

  return {
    t,
    roles,
    isAdmin,
    isQualitaetsmanager,
    isSuperAdmin,
    primaryRole: resolvePrimaryRole(roles),
    displayName: resolveDisplayName(profile),
    initials: resolveInitials(profile),
    signOut,
    isGuestMode,
    isTenantContext,
    tenantName,
    displayLogo,
    avatarProxyUrl,
    profileUpdatedAt,
    isOnline,
    isInProductionMode,
    openTasks,
    openInspections,
    openDefects,
    overdueTasksCount,
    overdueInspectionsCount,
    overdueDefectsCount,
    isTasksPulsing,
    isInspectionsPulsing,
    isDefectsPulsing,
  };
}
